import { Fragment } from "react";
import { Checkbox, Tag, TextInput } from "@carbon/react";
import { View } from "@carbon/icons-react";
import i18next from "../../i18n";
import { OrderResponse } from "../../types";
import {
  CurrencyTotal,
  currencyFormatter,
  lineSubtotal,
  linePriceInputsOf,
  offerLineTotals,
  pricedLinesOf,
} from "../../utils";
import { formatTotals } from "../LinePriceTable/LinePriceTable";
import { bidConditions } from "./bidConditions";
import AiRecommendedTag from "../OfferAssistant/AiRecommendedTag";
import { RecommendationMarks } from "../OfferAssistant/offerAssistantUtils";
import "./awardMatrix.scss";

/** One supplier's offers, one entry per material line they quoted (the matrix columns). */
export type MatrixCell = {
  offer: any;
  isBestOffer?: boolean;
  isBestAccumulate?: boolean;
};

type Props = {
  /** The material lines — the matrix ROWS. Single-order award passes a one-element array. */
  materials: OrderResponse[];
  /** One inner array per supplier — the matrix COLUMNS. */
  supplierGroups: MatrixCell[][];
  offersToAccept: any[];
  errors: any[];
  /** Column-header checkbox: award (or un-award) this supplier for every line at once. */
  onSupplierToggle: (group: MatrixCell[]) => void;
  /** Per-cell checkbox: split one line to a different supplier. */
  onCellToggle: (cell: MatrixCell, isSelected: boolean) => void;
  onVolumeChange: (cell: MatrixCell, volume: string) => void;
  onPriceChange: (cell: MatrixCell, price: string) => void;
  onViewDetail: (offer: any) => void;
  /** Offers the offer assistant recommended — marked "Recomendada por IA" with the reason. */
  aiMarks?: RecommendationMarks | null;
  /** A closed award, shown as it was decided: nothing can be selected or edited. */
  readOnly?: boolean;
};

/**
 * The award comparison matrix: SUPPLIERS as columns, MATERIALS as rows, so a supplier can be
 * awarded in their entirety (the column header selects all their lines) while still allowing a
 * per-line split (the cell checkboxes). Shared by the single ({@link Award}) and joint
 * ({@link AwardJoint}) panels — each wires the callbacks to its own reducer, so the award payload
 * stays byte-identical (AWARD-PAYLOAD PARITY). Carbon tokens throughout.
 */
const AwardMatrix = ({
  materials,
  supplierGroups,
  offersToAccept,
  errors,
  onSupplierToggle,
  onCellToggle,
  onVolumeChange,
  onPriceChange,
  onViewDetail,
  aiMarks = null,
  readOnly = false,
}: Props) => {
  const isSelected = (id: string) => offersToAccept.some((s) => s.id === id);

  // A request can put the same material on several lines: each row is its own LINE, so its cell is
  // the offer on that line. Matching by material only when the material is on one line alone.
  const linesPerMaterial = materials.reduce(
    (acc: Record<string, number>, line) => {
      const id = line.material?.id;
      if (id) acc[id] = (acc[id] ?? 0) + 1;
      return acc;
    },
    {}
  );

  const cellFor = (group: MatrixCell[], line: OrderResponse) =>
    group.find((c) => c?.offer?.order?.id === line.id) ??
    (linesPerMaterial[line.material?.id] === 1
      ? group.find((c) => c?.offer?.order?.material?.id === line.material?.id)
      : undefined);

  const supplierName = (group: MatrixCell[]) =>
    group.find((c) => c?.offer)?.offer?.user?.company?.name ?? "";

  const allSelected = (group: MatrixCell[]) => {
    const withOffer = group.filter((c) => c?.offer);
    return (
      withOffer.length > 0 && withOffer.every((c) => isSelected(c.offer.id))
    );
  };

  const isAiSingleSupplier = (group: MatrixCell[]) =>
    !!aiMarks?.bestSingle &&
    group.some((c) => aiMarks.bestSingle!.offerIds.includes(c?.offer?.id));

  // An offer's totals per currency: a line-priced service's from its lines (SCRUM-116), which
  // may use several currencies; any other offer's is its price × quantity, in its currency.
  const offerTotals = (offer: any): CurrencyTotal[] => {
    const fromLines = offerLineTotals(offer);
    return fromLines.length
      ? fromLines
      : [
          {
            currency: offer.currency,
            total: Number(offer.price) * Number(offer.order.quantity),
          },
        ];
  };

  const accumulatedTotals = (group: MatrixCell[]) =>
    group
      .filter((c) => c?.offer)
      .flatMap((c) => offerTotals(c.offer))
      .reduce((totals: CurrencyTotal[], t) => {
        const existing = totals.find((x) => x.currency === t.currency);
        return existing
          ? totals.map((x) =>
              x === existing ? { ...x, total: x.total + t.total } : x
            )
          : [...totals, { ...t }];
      }, []);

  return (
    <div className="award-cols__scroll">
      <table className="award-cols">
        <thead>
          <tr>
            <th className="award-cols__corner" />
            {supplierGroups.map((group, gi) => {
              const conditions = bidConditions(
                group.find((c) => c?.offer)?.offer
              );
              const best = group.some((c) => c?.isBestAccumulate);
              return (
                <th
                  key={`sup-${gi}`}
                  scope="col"
                  className={`award-cols__supplier${
                    best ? " award-cols__supplier--best" : ""
                  }`}
                >
                  <div className="award-cols__supplier-head">
                    <Checkbox
                      id={`award-supplier-${gi}`}
                      labelText={supplierName(group)}
                      checked={allSelected(group)}
                      disabled={readOnly}
                      onChange={() => onSupplierToggle(group)}
                    />
                    {best && (
                      <Tag type="green" size="sm">
                        {i18next.t("award.bestValue")}
                      </Tag>
                    )}
                  </div>
                  {isAiSingleSupplier(group) && (
                    <div className="award-cols__ai">
                      <AiRecommendedTag
                        labelKey="offerAssistant.bestSingleTag"
                        reason={aiMarks!.bestSingle!.reason}
                      />
                    </div>
                  )}
                  {conditions.length > 0 && (
                    <div className="award-cols__conditions">
                      {conditions.map((cond, i) => (
                        <span key={i}>{cond}</span>
                      ))}
                    </div>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {materials.map((material) => (
            <Fragment key={material.id}>
              <tr>
                <th scope="row" className="award-cols__material">
                  <div className="award-cols__material-name">
                    {material.material?.ean && (
                      <span className="award-cols__code">
                        {material.material.ean}
                      </span>
                    )}
                    {material.material?.globalMaterialName}
                  </div>
                  <div className="award-cols__material-qty">
                    {material.quantity} {material.measurementUnit}
                  </div>
                </th>
                {supplierGroups.map((group, gi) => {
                  const cell = cellFor(group, material);
                  if (!cell?.offer) {
                    return (
                      <td
                        key={`cell-${material.id}-${gi}`}
                        className="award-cols__cell award-cols__cell--empty"
                      >
                        —
                      </td>
                    );
                  }
                  const offer = cell.offer;
                  const current = offersToAccept.find((s) => s.id === offer.id);
                  const selected = !!current;
                  const hasError = errors.some(
                    (e) => e?.orderId === offer.order.id
                  );
                  const volume = current?.volume ?? offer.order.quantity;
                  const price = current?.price ?? offer.price;
                  return (
                    <td
                      key={`cell-${material.id}-${gi}`}
                      className={[
                        "award-cols__cell",
                        selected ? "award-cols__cell--selected" : "",
                        cell.isBestOffer && !selected
                          ? "award-cols__cell--best"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <div className="award-cols__cell-top">
                        <Checkbox
                          id={`award-cell-${material.id}-${offer.id}`}
                          labelText=""
                          aria-label={`${supplierName(group)} · ${
                            material.material?.globalMaterialName ?? ""
                          }`}
                          checked={selected}
                          disabled={readOnly}
                          onChange={() => onCellToggle(cell, selected)}
                        />
                        {cell.isBestOffer && (
                          <Tag type="green" size="sm">
                            {i18next.t("award.bestValue")}
                          </Tag>
                        )}
                        <button
                          type="button"
                          className="award-cols__detail"
                          aria-label={i18next.t("award.offerDetails")}
                          onClick={() => onViewDetail(offer)}
                        >
                          <View size={16} />
                        </button>
                      </div>

                      {aiMarks && offer.id in aiMarks.reasons && (
                        <div className="award-cols__ai">
                          <AiRecommendedTag
                            reason={aiMarks.reasons[offer.id]}
                          />
                        </div>
                      )}

                      {selected && readOnly ? (
                        <div className="award-cols__price">
                          {i18next.t("award.awardedVolume")}: {volume}{" "}
                          {material.measurementUnit} ·{" "}
                          {currencyFormatter(Number(price), offer.currency)}
                        </div>
                      ) : selected ? (
                        <div className="award-cols__inputs">
                          <TextInput
                            id={`award-vol-${offer.id}`}
                            size="sm"
                            labelText={i18next.t("award.awardedVolume")}
                            value={String(volume)}
                            onChange={(e) =>
                              onVolumeChange(cell, e.target.value)
                            }
                          />
                          <TextInput
                            id={`award-price-${offer.id}`}
                            size="sm"
                            labelText={i18next.t("award.unitPrice")}
                            value={String(price)}
                            onChange={(e) =>
                              onPriceChange(cell, e.target.value)
                            }
                          />
                        </div>
                      ) : offerLineTotals(offer).length ? (
                        <div className="award-cols__price">
                          {formatTotals(offerLineTotals(offer))}
                        </div>
                      ) : (
                        <div className="award-cols__price">
                          {currencyFormatter(
                            Number(offer.price),
                            offer.currency
                          )}{" "}
                          <span className="award-cols__per">
                            / {material.measurementUnit}
                          </span>
                        </div>
                      )}

                      <div
                        className={`award-cols__total${
                          hasError ? " award-cols__total--error" : ""
                        }`}
                      >
                        {offerLineTotals(offer).length > 1
                          ? formatTotals(offerLineTotals(offer))
                          : currencyFormatter(
                              (Number(volume) || 0) * (Number(price) || 0),
                              offer.currency
                            )}
                      </div>
                    </td>
                  );
                })}
              </tr>
              {/* SCRUM-116: a service quoted by line shows what each supplier charges per line. */}
              {pricedLinesOf(material).map((line) => (
                <tr
                  key={`${material.id}-${line.code}`}
                  className="award-cols__line-row"
                >
                  <th
                    scope="row"
                    className="award-cols__material award-cols__line"
                  >
                    <div className="award-cols__material-name">
                      <span className="award-cols__code">{line.code}</span>
                      {line.description}
                    </div>
                    <div className="award-cols__material-qty">
                      {line.quantity}{" "}
                      {i18next.t(`serviceDetail.unitShort.${line.unit}`)}
                    </div>
                  </th>
                  {supplierGroups.map((group, gi) => {
                    const offer = cellFor(group, material)?.offer;
                    const linePrice = offer
                      ? linePriceInputsOf(
                          pricedLinesOf(material),
                          offer.linePrices,
                          offer.currency
                        )[line.code!]
                      : undefined;
                    const unitPrice =
                      Number(linePrice?.unitPrice) > 0
                        ? linePrice!.unitPrice
                        : undefined;
                    return (
                      <td
                        key={`line-${material.id}-${line.code}-${gi}`}
                        className="award-cols__cell award-cols__line"
                      >
                        {unitPrice ? (
                          <>
                            <div className="award-cols__price">
                              {currencyFormatter(
                                Number(unitPrice),
                                linePrice!.currency
                              )}{" "}
                              <span className="award-cols__per">
                                × {line.quantity}
                              </span>
                            </div>
                            <div className="award-cols__total">
                              {currencyFormatter(
                                lineSubtotal(line, unitPrice),
                                linePrice!.currency
                              )}
                            </div>
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </Fragment>
          ))}

          <tr className="award-cols__total-row">
            <th scope="row" className="award-cols__material">
              {i18next.t("tenders.total")}
            </th>
            {supplierGroups.map((group, gi) => (
              <td key={`tot-${gi}`} className="award-cols__cell">
                <div className="award-cols__grand">
                  {formatTotals(accumulatedTotals(group))}
                </div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default AwardMatrix;
